import {
  addDays,
  differenceInDays,
  isAfter,
  isSameDay,
  subDays,
} from "date-fns";
import { MusicGenre } from "./MusicGenre";
import { Theme } from "./Theme";
import {
  DateFactory,
  DatePeriod,
  Day,
  hasNewYearsEvePeriod,
  isNewYearsDay,
  isNewYearsEve,
  LenientDatePeriod,
  TimeOfDay,
} from "@greeter/date";
import { Address, AddressSchema } from "./Address";
import { WeeklyOpeningHours } from "./WeeklyOpeningHours";
import { ImageAsset, ImageAssetSchema } from "./ImageAsset";
import { SpecialOpeningHours } from "./SpecialOpeningHour";
import { convertToDay } from "@greeter/date";
import * as z from "zod";
import { JsonMapSchema } from "@greeter/json";
import { Log } from "@greeter/log";

const TimeOfDayPeriodSchema = z.object({
  from: z.string().transform((s) => TimeOfDay.parse(s)),
  to: z.string().transform((s) => TimeOfDay.parse(s)),
  isOpen: z.optional(z.boolean()).transform((b) => b ?? false),
});

/**
 * Schema to support deserializing from
 * { "__type": "Map", value: Array<[any, any]> }
 * as well as the classic { key: value } object
 * way of doing things as well as Array<[any, any]> directly.
 */
export const WeeklyOpeningHoursSchema = JsonMapSchema(
  z
    .number()
    .or(z.string())
    .transform((v) => {
      if (typeof v === "string") {
        return convertToDay(v);
      } else {
        return v;
      }
    }),
  TimeOfDayPeriodSchema,
).transform((o) => {
  return new WeeklyOpeningHours(o);
});

export const VenueIdSchema = z.string();
export type VenueId = z.infer<typeof VenueIdSchema>;
export const VenueSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    address: AddressSchema,
    logoUrl: z.string(),
    logoAsset: ImageAssetSchema,
    coverUrl: z.string(),
    coverAsset: ImageAssetSchema,
    cvr: z.string(),
    verified: z.boolean(),
    dressCode: z.string(),
    launchDate: z.string().transform((s) => new Date(s)),
    weeklyOpeningHours: WeeklyOpeningHoursSchema.optional(),
    weeklyArrivalOpeningHours: WeeklyOpeningHoursSchema.optional(),
    bookableOpeningHours: WeeklyOpeningHoursSchema,
    musicGenres: z.array(z.any()),
    customUri: z.string().nullable(),
    themes: z.array(z.any()),
  })
  .transform(({ weeklyArrivalOpeningHours, weeklyOpeningHours, ...rest }) => {
    const mapped: Venue = {
      ...rest,
      arrivalOpeningHours: new WeeklyOpeningHours(),
      openingHours: new WeeklyOpeningHours(),
    };

    if (weeklyArrivalOpeningHours) {
      mapped.arrivalOpeningHours = weeklyArrivalOpeningHours;
    }
    if (weeklyOpeningHours) {
      mapped.openingHours = weeklyOpeningHours;
    }
    return mapped;
  });

// export type Venue = z.infer<typeof VenueSchema>;
export type Venue = {
  id: string;
  name: string;
  description: string;
  address: Address;
  logoUrl: string;
  logoAsset: ImageAsset;
  coverUrl: string;
  coverAsset: ImageAsset;
  cvr: string;
  verified: boolean;
  dressCode: string;
  launchDate: Date;
  customUri: string | null;
  openingHours: WeeklyOpeningHours;
  arrivalOpeningHours: WeeklyOpeningHours;
  bookableOpeningHours: WeeklyOpeningHours;
  musicGenres: MusicGenre[];
  themes: Theme[];
};

export module Venue {
  export function createDefault(): Venue {
    return {
      id: "Placeholder",
      name: "Greeter Venue",
      description: "Dette er et venue på Greeter",
      address: Address.createDefault(),
      logoUrl: "",
      logoAsset: {
        id: "",
        name: "",
        root: "",
        path: "",
        uri: "",
        sizes: [],
      },
      coverUrl: "",
      coverAsset: {
        path: "",
        uri: "",
        id: "",
        name: "",
        root: "",
        sizes: [],
      },
      cvr: "12345678",
      verified: true,
      dressCode: "Ingen",
      launchDate: DateFactory.create(),
      openingHours: new WeeklyOpeningHours(),
      arrivalOpeningHours: new WeeklyOpeningHours(),
      bookableOpeningHours: new WeeklyOpeningHours(),
      customUri: null,
      musicGenres: [],
      themes: [],
    };
  }

  export function isOpenToday(self: Venue) {
    if (!isLaterThanLaunchDate(self)) return false;

    const now = DateFactory.create();
    const nowAsDay = now.getDay();
    const openToday = self.openingHours.get(nowAsDay);
    return !!openToday;
  }

  export function isOpenNow(self: Venue) {
    if (!isLaterThanLaunchDate(self)) return false;

    const now = DateFactory.create();
    const nowAsDay = now.getDay();
    const todayOpeningHours = self.openingHours.get(nowAsDay);

    if (todayOpeningHours) {
      return TimeOfDay.fromDate(now).isWithin(
        todayOpeningHours.from,
        todayOpeningHours.to,
      );
    } else {
      return false;
    }
  }

  export function isLaterThanLaunchDate(self: Venue) {
    const now = DateFactory.create();
    return isAfter(now, self.launchDate);
  }

  export type CreateDatePeriodsFromOpeningHoursArgs = {
    openingHours: WeeklyOpeningHours;
    specialOpeningHours: Array<SpecialOpeningHours>;
    range: number | DatePeriod;
    now: Date;
  };

  /**
   * Creates DatePeriods for opening hours with settable range and now anchor.
   *
   * TODO: Create a generator version?
   */
  export function createDatePeriodsFromOpeningHours(
    args: CreateDatePeriodsFromOpeningHoursArgs,
  ): Array<LenientDatePeriod> {
    const datePeriods: Array<LenientDatePeriod> = [];
    const now = args.now;
    const openingHours = args.openingHours;
    const hasNewYearsEve = hasNewYearsEvePeriod(
      args.specialOpeningHours.map((s) => s.period),
    );

    const l =
      typeof args.range === "number"
        ? args.range
        : differenceInDays(args.range.to, args.range.from);

    for (let i = 0; i < l; i++) {
      const date = addDays(now, i);

      const openingHoursForDate = openingHours.get(date.getDay() as Day);
      // FUTURE: Discuss special opening hours
      //
      // This is a poor mans upcasting. In case the place has special rules for the floor,
      // we adhere to those instead.
      //
      // This is open to discussion once we figure out how all of this should fit together
      // in the bigger picture. The areas should be able to adhere to special opening hours
      // as well.
      //
      // This makes sense since most venues have a 1. floor/area in which is ALWAYS open
      // and where the 2. floor/area should be closed until later in the evening. It is
      // however rather confusing
      const hasInheritance =
        openingHoursForDate &&
        "isInherited" in openingHoursForDate &&
        typeof openingHoursForDate.isInherited === "boolean";
      const isInherited = hasInheritance && openingHoursForDate.isInherited;

      /**
       * We want to mark special opening hours
       */
      const specialOpeningHours = args.specialOpeningHours.find((soh, i) => {
        /**
         * New years eve is a special date
         * where usually the opening hours HAVE to
         * be input as 00:00+ - sometime, on the 1. of january
         *
         * This might cause a lot of confusion for the customer since
         * they are thinking of the 31st and NOT the 1st of january
         */
        const isNewYearsDayAndNewYearsEve =
          isNewYearsDay(date) && isNewYearsEve(soh.period.from);
        const isSameDayNotNewYearsEve =
          isSameDay(date, soh.period.from) && !isNewYearsEve(soh.period.from);

        return isSameDayNotNewYearsEve || isNewYearsDayAndNewYearsEve;
      });

      if (specialOpeningHours && (!hasInheritance || isInherited)) {
        datePeriods.push(new LenientDatePeriod(specialOpeningHours.period));

        const skipForward = differenceInDays(
          specialOpeningHours.period.to,
          specialOpeningHours.period.from,
        );
        // In case of multiday span, if that ever happens,
        // should result in 0 on days less than 24 hours
        i += skipForward;
        continue;
      }

      if (!openingHoursForDate || !openingHoursForDate.isOpen) {
        continue;
      }

      const period = LenientDatePeriod.fromTimePeriod(
        date,
        openingHoursForDate.from,
        openingHoursForDate.to,
      );
      datePeriods.push(period);

      // Log.debug("[createDatePeriodFromOpeningHours] after adding period", {
      //   specialOpeningHours,
      //   date,
      //   period,
      // });
    }

    return datePeriods;
  }

  export type CreateOpeningHoursDatePeriodForDateArgs = {
    targetDate: Date;
    openingHours: WeeklyOpeningHours;
    specialOpeningHours: Array<SpecialOpeningHours>;
  };
  export function createOpeningHoursDatePeriodForDate(
    args: CreateOpeningHoursDatePeriodForDateArgs,
  ): LenientDatePeriod | undefined {
    const datePeriod = createDatePeriodsFromOpeningHours({
      specialOpeningHours: args.specialOpeningHours,
      openingHours: args.openingHours,
      now: args.targetDate,
      range: 10,
    }).find((dp) => isSameDay(dp.from, args.targetDate));
    return datePeriod;
  }

  /**
   * This gets the opening hours based on the date. The only thing
   * considered is year, month and date and finds the opening hours
   * which start matches the date.
   */
  export function findOpeningHoursAsDatePeriodForDate(
    venue: Venue,
    date: Date,
    specialOpeningHours: Array<SpecialOpeningHours>,
  ): LenientDatePeriod | undefined {
    const datePeriods = Venue.createDatePeriodsFromOpeningHours({
      openingHours: venue.bookableOpeningHours,
      specialOpeningHours: specialOpeningHours,
      range: 10,
      now: subDays(date, 1),
    });

    for (const datePeriod of datePeriods) {
      Log.debug("[findOpeningHoursAsDatePeriodForDate]", {
        date,
        datePeriod,
        datePeriods,
        "isNewYearsDay(date)": isNewYearsDay(date),
        "isNewYearsEve(date)": isNewYearsEve(date),
        "isNewYearsEve(datePeriod.from)": isNewYearsEve(datePeriod.from),
        "isSameDay(datePeriod.from, date)": isSameDay(datePeriod.from, date),
      });
      if (isNewYearsEve(date) && isNewYearsEve(datePeriod.from)) {
        return datePeriod;
      }
      if (
        isSameDay(datePeriod.from, date) &&
        !isNewYearsDay(date) &&
        !isNewYearsEve(date) &&
        !isNewYearsEve(datePeriod.from)
      ) {
        return datePeriod;
      }
    }

    return;
  }

  /**
   * This gets the opening hours based on the date. The only thing
   * considered is year, month and date and finds the opening hours
   * which start matches the date.
   */
  export function findOpeningHoursAsDatePeriodForDateAndTime(
    venue: Venue,
    date: Date,
    specialOpeningHours: Array<SpecialOpeningHours>,
  ): LenientDatePeriod | undefined {
    const datePeriods = Venue.createDatePeriodsFromOpeningHours({
      openingHours: venue.bookableOpeningHours,
      specialOpeningHours: specialOpeningHours,
      range: 10,
      now: subDays(date, 1),
    });

    for (const datePeriod of datePeriods) {
      if (datePeriod.isWithin(date)) {
        return datePeriod;
      }
    }

    return;
  }
}
